<template>
  <a-modal title="Change Rate" v-if="visible" :visible="visible" :destroy-on-close="true" @cancel="closeModal">
    <actualize-modal
      :visible.sync="visibleActualizeModal"
      :show-support="false"
      @update="closeModal"
      @close="closeModal"
    />
    <a-form-model
      layout="horizontal"
      ref="form"
      :model="form"
      :rules="rules"
      :label-col="{ span: 8 }"
      :wrapper-col="{ span: 16 }"
    >
      <a-form-model-item :colon="false" label-align="left" label="Reason" prop="reason">
        <a-textarea v-model="form.reason" placeholder="Enter Reason" /> </a-form-model-item
      ><a-form-model-item
        :colon="false"
        label-align="left"
        label="Applied At"
        prop="applied_at"
        :extra="`Time zone: ${timeZone}`"
      >
        <a-date-picker
          v-model="form.applied_at"
          class="w-full"
          :disabled="!!record"
          :format="dateTimeFormat"
          :show-time="timePickerOptions"
        />
      </a-form-model-item>

      <a-form-model-item :colon="false" label-align="left" label="Payment Method" prop="method">
        <a-select
          v-model="form.method"
          placeholder="Enter payment method"
          :disabled="!!record"
          @change="clearNewPrimaryCompensation"
        >
          <a-select-option
            v-for="(value, key) in paymentMethods"
            :key="key"
            :disabled="isDisabledPaymentMethod && key === 'marketplace_rate'"
            :value="key"
          >
            {{ value }}
          </a-select-option>
        </a-select>
      </a-form-model-item>
      <a-form-model-item
        v-if="isVisibleRate && form.method !== 'salary'"
        :colon="false"
        label="Hourly Rate, $"
        label-align="left"
        prop="hourly_rate"
      >
        <a-input-number :disabled="!!record" v-model="rateProxy" :min="0" placeholder="Enter rate" />
        <div v-if="hourlyRateMinimumWarning" class="minimum-pay-warning">
          {{ hourlyRateMinimumWarning }}
        </div> </a-form-model-item
      ><a-form-model-item
        v-if="form.method === 'salary'"
        :colon="false"
        label="Salary for Pay Period, $"
        label-align="left"
        prop="salary"
      >
        <a-input-number :disabled="!!record" v-model="salaryProxy" :min="0" placeholder="Enter salary" />
        <div v-if="salaryMinimumWarning" class="minimum-pay-warning">
          {{ salaryMinimumWarning }}
        </div>
      </a-form-model-item>
      <a-form-model-item :colon="false" label="Orientation Rate, $" label-align="left" prop="orientation_rate">
        <a-input-number
          :disabled="!!record"
          v-model="orientationRateProxy"
          :min="0"
          placeholder="Enter Orientation Rate"
        />
      </a-form-model-item>

      <a-form-model-item :colon="false" label="Training Rate, $" label-align="left" prop="training_rate">
        <a-input-number :disabled="!!record" v-model="trainingRateProxy" :min="0" placeholder="Enter Training Rate" />
      </a-form-model-item>
    </a-form-model>

    <template slot="footer">
      <div class="flex justify-content-between">
        <a-button @click="closeModal">Cancel</a-button>
        <a-button @click="handleSubmit" type="primary" :loading="loading">Save</a-button>
      </div>
    </template>
  </a-modal>
</template>

<script>
import { PAYMENT_METHOD } from '@/store/modules/payment/constants'
import { getMinimumPayWarnings } from '@/modules/profile/utils/minimum-pay'
import { deepCopy } from '@/utils/deep-copy'
import { Button, DatePicker, FormModel, Input, InputNumber, Modal, notification, Select } from 'ant-design-vue'
import moment from 'moment-timezone'
import { createNamespacedHelpers } from 'vuex'
import ProfileActualizeShift from '../profile-actualize-shift.vue'

const { mapActions: profileActions, mapState: profileState } = createNamespacedHelpers('profile')

const initialForm = {
  method: undefined,
  hourly_rate: 0,
  training_rate: 0,
  orientation_rate: 0,
  salary: 0,
  reason: undefined,
  applied_at: undefined
}

const initialRules = {
  reason: [
    {
      required: true,
      message: 'Reason date can not be empty',
      trigger: 'change'
    }
  ],
  applied_at: [
    {
      required: true,
      message: 'Applied date can not be empty',
      trigger: 'change'
    }
  ],
  method: [
    {
      required: true,
      message: 'Payment method can not be empty',
      trigger: 'change'
    }
  ],
  hourly_rate: [
    {
      required: true,
      message: 'Rate can not be empty',
      trigger: 'change',
      type: 'number'
    },
    {
      min: 1,
      message: 'Rate can not be empty',
      trigger: 'change',
      type: 'number'
    }
  ],
  salary: [
    {
      required: true,
      message: 'Salary can not be empty',
      trigger: 'change',
      type: 'number'
    },
    {
      min: 1,
      message: 'Salary can not be empty',
      trigger: 'change',
      type: 'number'
    }
  ]
}

export default {
  components: {
    'a-modal': Modal,
    'a-form-model-item': FormModel.Item,
    'a-form-model': FormModel,
    'a-input-number': InputNumber,
    'a-select': Select,
    'a-select-option': Select.Option,
    'a-button': Button,
    'a-date-picker': DatePicker,
    'a-textarea': Input.TextArea,
    'actualize-modal': ProfileActualizeShift
  },
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    record: {
      type: Object,
      default: undefined
    }
  },
  data() {
    return {
      form: { ...initialForm },
      rules: { ...initialRules },

      loading: false,
      paymentMethods: PAYMENT_METHOD,

      visibleActualizeModal: false
    }
  },
  watch: {
    visible: {
      handler(newVal) {
        if (newVal) {
          const { payment_info } = this.associate

          const { legal_entity, pay_rate, project_id, status, type, ...rest } = payment_info

          this.form = { ...initialForm, ...rest }
          this.form.training_rate = rest.training_rate ?? 0
          this.form.orientation_rate = rest.orientation_rate ?? 0

          if (!payment_info.hourly_rate || (!payment_info.salary && payment_info.method === 'salary')) {
            this.form.reason = 'Initial Base Rate'
            this.form.applied_at = moment(this.associate.hire_date)
              .utc(true)
              .tz(this.$store.state.applicationState.timeZone)
          }

          this.fillDefaultRates()

          if (this.record) {
            this.form = deepCopy({ ...this.record, reason: undefined })
          }
        }

        if (!newVal) {
          this.form = { ...initialForm }
        }
      },
      immediate: true
    }
  },
  computed: {
    ...profileState({
      associate: state => state.associate
    }),

    visualDateFormat() {
      return this.$store.state.applicationState.dateFormat
    },

    dateTimeFormat() {
      return `${this.visualDateFormat} h:mm A`
    },

    timePickerOptions() {
      return {
        format: 'h:mm A',
        use12Hours: true,
        defaultValue: moment().startOf('day')
      }
    },

    timeZone() {
      return this.$store.state.applicationState.timeZone
    },

    roles() {
      return this.$store.state.applicationState.projectRoles
    },

    isDisabledPaymentMethod() {
      return !(
        this.associate.payment_info.type === 'Contractor' && this.$store.state.applicationState.currentProject.staffing
      )
    },

    isVisibleRate() {
      switch (this.associate?.payment_info?.type) {
        case 'Employee': {
          return !!this.form.method
        }
        case 'Volunteer': {
          return false
        }
        case 'Contractor': {
          return true
        }
        default:
          return false
      }
    },

    projectPaymentSettings() {
      return this.$store.state.applicationState.currentProject?.payment_settings ?? {}
    },

    minimumPayWarnings() {
      if (this.record) {
        return {}
      }

      const paymentInfo = this.associate?.payment_info ?? {}

      return getMinimumPayWarnings(
        {
          ...paymentInfo,
          ...this.form
        },
        this.projectPaymentSettings
      )
    },

    hourlyRateMinimumWarning() {
      return this.minimumPayWarnings.hourly_rate
    },

    salaryMinimumWarning() {
      return this.minimumPayWarnings.salary
    },

    rateProxy: {
      get() {
        return this.form.hourly_rate / 100
      },
      set(value) {
        this.form.hourly_rate = Math.round(value * 100)
      }
    },

    orientationRateProxy: {
      get() {
        return this.form.orientation_rate / 100
      },
      set(value) {
        this.form.orientation_rate = Math.round(value * 100)
      }
    },

    trainingRateProxy: {
      get() {
        return this.form.training_rate / 100
      },
      set(value) {
        this.form.training_rate = Math.round(value * 100)
      }
    },

    salaryProxy: {
      get() {
        return this.form?.salary / 100
      },
      set(value) {
        this.form.salary = Math.round(value * 100)
      }
    },

    isCafChange() {
      if (this.associate?.payment_info?.type !== 'Employee') {
        return false
      }

      const currentPayment = this.associate.payment_info
      return (
        currentPayment.method !== this.form.method ||
        this.getPrimaryCompensation(currentPayment) !== this.getPrimaryCompensation(this.form)
      )
    }
  },
  methods: {
    ...profileActions(['postRates', 'reloadAssociate']),

    showNotification(type, message, description) {
      notification[type]({
        message,
        description
      })
    },
    convertToServerFormat(date) {
      return moment(date).tz(this.timeZone, true).toISOString()
    },
    getPrimaryCompensation({ method, hourly_rate, salary }) {
      return method === 'salary' ? salary : hourly_rate
    },
    clearNewPrimaryCompensation(method) {
      if (method === 'salary') {
        this.form.salary = 0
        return
      }

      this.form.hourly_rate = 0
    },
    formatPrimaryCompensation(payment) {
      const amount = this.getPrimaryCompensation(payment) ?? 0
      const unit = payment.method === 'salary' ? 'per pay period' : 'per hour'
      return `$${(amount / 100).toFixed(2)} ${unit}`
    },
    formatAppliedAt(date) {
      return moment(date).format(this.dateTimeFormat)
    },
    closeModal() {
      this.$emit('update:visible', false)
      this.$emit('update:record', undefined)

      this.form = { ...initialForm }
    },
    fillDefaultRates() {
      const currentRole = this.roles.find(role => role.id === this.associate.role.id)
      if (!currentRole) return

      const fields = ['hourly_rate', 'orientation_rate', 'training_rate']

      fields.forEach(field => {
        if (!this.form[field]) {
          this.form[field] = currentRole[field] ?? 0
        }
      })
    },
    async openActualizeShift() {
      Modal.confirm({
        title: 'Review unpaid shift rates',
        content: 'Choose a rate and period, then review the changes before applying them to unpaid shifts.',
        okText: 'Review rates',
        cancelText: 'Close',
        onOk: () => {
          this.visibleActualizeModal = true
        },
        onCancel: () => {
          this.closeModal()
        }
      })
    },
    handleSubmit() {
      this.$refs.form.validate(async valid => {
        if (!valid) return

        if (this.isCafChange) {
          this.confirmCafChange()
          return
        }

        await this.submitRateChange()
      })
    },
    confirmCafChange() {
      const currentPayment = this.associate.payment_info
      Modal.confirm({
        title: 'Send change action form?',
        content: `Current: ${PAYMENT_METHOD[currentPayment.method]} — ${this.formatPrimaryCompensation(
          currentPayment
        )}. New: ${PAYMENT_METHOD[this.form.method]} — ${this.formatPrimaryCompensation(this.form)}. Applied At: ${this.formatAppliedAt(
          this.form.applied_at
        )} (${this.timeZone}). Reason: ${this.form.reason}. The new rate will be applied now. A signature request and work restriction will be added immediately.`,
        okText: 'Send form',
        cancelText: 'Cancel',
        onOk: () => this.submitRateChange({ rethrow: true })
      })
    },
    async submitRateChange({ rethrow = false } = {}) {
      const isCafChange = this.isCafChange

      try {
        this.loading = true

        const payload = {
          associate_id: this.associate.id,
          form: {
            ...this.form,
            applied_at: this.convertToServerFormat(this.form.applied_at),
            timezone: this.timeZone
          }
        }

        const data = await this.postRates(payload)

        if (isCafChange) {
          this.closeModal()
          this.reloadAssociate(true)
          this.showNotification(
            'success',
            'Rate updated and change action form sent',
            'The new rate is active. The associate must sign the document to acknowledge the change.'
          )
        } else {
          this.showNotification('success', 'Success', 'Rates has been updated successfully')
          this.openActualizeShift()
        }

        this.$emit('update', data)
      } catch (error) {
        this.showNotification('error', 'Error', typeof error === 'string' ? error : error?.message)
        if (rethrow) {
          throw error
        }
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style scoped>
.ant-row {
  display: flex;
  margin: 0 !important;
}

.ant-form {
  display: flex;
  justify-content: center;
  flex-direction: column;
  gap: 8px;
}

::v-deep .ant-input-number {
  width: 100%;
}

.minimum-pay-warning {
  color: #ef4444;
  font-size: 12px;
  line-height: 1.4;
  margin-top: 4px;
}
</style>
